import type { Timeframe } from "@/api/types";

/**
 * Returns the bar's `time` (UTC seconds — what lightweight-charts
 * expects) for the bucket the given millisecond timestamp belongs to.
 *
 * Intraday timeframes use a simple modulo on milliseconds.
 * HTF (1D/1W/1M/1Y) need calendar arithmetic to match the boundaries
 * `generate_htf_from_daily.py` uses (UTC midnight / Monday / 1st / Jan 1).
 */
export function bucketStartSec(timeMsc: number, tf: Timeframe): number {
  const d = new Date(timeMsc);
  switch (tf) {
    case "1D":
      return Math.floor(
        Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 1000,
      );
    case "1W": {
      // Monday as week start (matches htf.json)
      const dow = d.getUTCDay(); // Sun=0, Mon=1, …, Sat=6
      const daysFromMonday = (dow + 6) % 7; // Mon→0, Sun→6
      return Math.floor(
        Date.UTC(
          d.getUTCFullYear(),
          d.getUTCMonth(),
          d.getUTCDate() - daysFromMonday,
        ) / 1000,
      );
    }
    case "1M":
      return Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) / 1000);
    case "1Y":
      return Math.floor(Date.UTC(d.getUTCFullYear(), 0, 1) / 1000);
    default: {
      const ms = INTRADAY_MS[tf];
      return Math.floor(timeMsc / ms) * (ms / 1000);
    }
  }
}

const INTRADAY_MS: Record<string, number> = {
  "1min": 60_000,
  "5min": 5 * 60_000,
  "15min": 15 * 60_000,
  "30min": 30 * 60_000,
  "1H": 60 * 60_000,
  "2H": 2 * 60 * 60_000,
  "4H": 4 * 60 * 60_000,
};
